import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ReactTooltip from 'react-tooltip'
import { SimulationSteps } from './types'
import LookingForHelp from 'components/looking-for-help/LookingForHelp'
import {
  BuildingTypeStep,
  BuyerTypeStep,
  EnergyStep,
  HasSolarPanelsStep,
  KnowsUsageStep,
  MeterTypeStep,
  PostalCodeStep,
  ResidentsStep,
  CounterTypeStep,
  SolarPowerStep,
  SubscribeStep,
  UsageStep
} from './steps'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from 'store/types'
import { updateSimulationInput } from 'store/simulation/actions/updateSimulationInput'
import { SimulationInputKeys } from 'store/simulation/types'
import { getPriceSimulation } from './utils'
import styles from './Simulation.module.scss'
import modalStyles from 'components/modal/Modal.module.scss'
import popupStyles from './components/simulation-popup/SimulationPopup.module.scss'
import LoadingDots from 'components/loading-dots/LoadingDots'
import classNames from 'classnames'
import { hideSimulation } from 'store/simulation/actions/hideSimulation'
import { useRouter } from 'next/router'
import { getSimulationRedirectRoute } from 'utils/routes'
import { toggleContactMe } from 'store/simulation/actions/toggleContactMe'
import { SimulationPropTypes as Props } from './types'
import { SimulationType } from 'store/marketing/types'
import { Language } from 'types/language'

export const Simulation: FC<Props> = ({ endSimulation }) => {
  // REDUX STORE
  const { opportunityId } = useSelector((store: Store) => store.app)
  const { noOptinOne, simulationType } = useSelector((store: Store) => store.marketing)
  const { simulation } = useSelector((store: Store) => store)
  const dispatch = useDispatch()

  // ROUTER
  const router = useRouter()
  const { locale, route } = router
  const isProducerOverviewPage = route === '/producers'
  const isProducerDetailPage = route === '/producers/[slug]'
  const isProducerRegistrationPage = route === '/producers/[slug]/registration'

  // i18n
  const { t } = useTranslation('simulation')

  // Constants
  const isNotDefaultPricingSimulation = simulationType !== SimulationType.PRICING_SIMULATION

  /**
   * Triggered on every currentStep store change
   */
  useEffect(() => {
    // Fetch the simulation from the API if the current step is Subscribe & we're not coming from the contact me button
    if (simulation.currentStep === SimulationSteps.SUBSCRIBE && !simulation.contactMe) {
      getPriceSimulation()

      // Hide the simulation if opportunity is empty or the no_optin_one url param was set
      if ((opportunityId && opportunityId !== '') || noOptinOne || isNotDefaultPricingSimulation) dispatch(hideSimulation())

      // Redirect to producers page of no_optin_url param was set & we're not on producer overview, detail or registration page
      if (
        (noOptinOne || isNotDefaultPricingSimulation) &&
        !isProducerOverviewPage &&
        !isProducerDetailPage &&
        !isProducerRegistrationPage
      ) {
        router.push(getSimulationRedirectRoute(locale as Language))
      }
    }

    // Toggle the contactMe in the store if it is set to true
    if (simulation.contactMe) dispatch(toggleContactMe())
  }, [simulation.currentStep])

  /**
   * Triggers the GTM event & sets the email, firstname and lastname if they are specified in the URL
   * Only triggerd on first render
   */
  useEffect(() => {
    // Set email, firstname & lastname in the store
    const search = new URLSearchParams(window.location.search)
    if (search.has('email')) dispatch(updateSimulationInput({ key: SimulationInputKeys.EMAIL, value: search.get('email') }))
    if (search.has('firstname')) dispatch(updateSimulationInput({ key: SimulationInputKeys.FIRSTNAME, value: search.get('firstname') }))
    if (search.has('lastname')) dispatch(updateSimulationInput({ key: SimulationInputKeys.LASTNAME, value: search.get('lastname') }))
  }, [])

  /**
   * Returns the content for the current step in the redux simulation store
   */
  const getContentForCurrentStep = () => {
    switch (simulation.currentStep) {
      case SimulationSteps.POSTAL_CODE:
        return <PostalCodeStep />

      case SimulationSteps.BUYER_TYPE:
        return <BuyerTypeStep />

      case SimulationSteps.COUNTER_TYPE:
        return <CounterTypeStep />

      case SimulationSteps.HAS_SOLAR_PANELS:
        return <HasSolarPanelsStep />

      case SimulationSteps.SOLAR_POWER:
        return <SolarPowerStep />

      case SimulationSteps.ENERGY_TYPE:
        return <EnergyStep />

      case SimulationSteps.KNOWS_USAGE:
        return <KnowsUsageStep />

      case SimulationSteps.METER_TYPE:
        return <MeterTypeStep />

      case SimulationSteps.BUILDING_TYPE:
        return <BuildingTypeStep />

      case SimulationSteps.RESIDENTS:
        return <ResidentsStep />

      case SimulationSteps.USAGE:
        return <UsageStep />

      case SimulationSteps.SUBSCRIBE:
        return <SubscribeStep endSimulation={endSimulation} />

      default:
        return null
    }
  }

  return (
    <div className={classNames(modalStyles.content, popupStyles.content)}>
      {simulation.isLoading ? (
        <div className="has-text-centered">
          <div className="margin-bottom-m">
            <LoadingDots />
          </div>

          <p className="margin-top-none margin-bottom-none font-size-xl font-weight-bold is-color-text">
            {t('calculatingYourPrice', 'We berekenen je prijs...')}
          </p>
        </div>
      ) : (
        <>
          <div className="has-text-centered">
            <h3 className="is-grey is-light margin-top-none margin-bottom-l font-size-base make-your-simulation">
              {t('makeYourSimulation', 'Maak je persoonlijke simulatie')}
            </h3>
          </div>

          <div className={styles['simulation-block']}>
            {getContentForCurrentStep()}
            <LookingForHelp isSimulation />
          </div>

          <ReactTooltip id="simulation" uuid="simulation-uuid" effect="solid" />
        </>
      )}
    </div>
  )
}

export default Simulation
